import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Cpu, Upload, CheckCircle, AlertTriangle, Zap, Loader2 } from 'lucide-react';
import { useDatabase } from '../../context/DatabaseContext';
import { useDataTransfer } from '../../context/DataTransferContext';
import { simulationTagApi } from '../../services/api';
import StuiSelect from '../../components/common/StuiSelect';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function triggerDownload(content, filename) {
  if (!content) return;
  let blob;
  if (filename?.endsWith('.xlsx')) {
    const bytes = Uint8Array.from(atob(content), c => c.charCodeAt(0));
    blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  } else {
    blob = new Blob([content], { type: 'text/plain' });
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// name/format/rows for each of the up-to-3 auto-downloaded files, keyed off the generate result.
function describeOutputs(result) {
  const fmt = (name) => (name?.endsWith('.xlsx') ? 'excel' : name?.endsWith('.csv') ? 'csv' : 'txt');
  const out = [
    { content: result.fileA, filename: result.fileNameA, rows: result.simTagCount },
    { content: result.fileB, filename: result.fileNameB, rows: result.stFunctionCount },
  ];
  if (result.fileC) out.push({ content: result.fileC, filename: result.fileNameC, rows: result.simTagCount });
  return out.map(o => ({ ...o, format: fmt(o.filename) }));
}

// ─── CMD_A Wiring Popup (Mode B) ─────────────────────────────────────────────

function WiringPopup({ candidates, cmdAMap, setCmdAMap, onGenerate, onClose, generating, error }) {
  const [plcName, setPlcName] = useState('');

  const handleGenerate = () => onGenerate(plcName, cmdAMap);

  return (
    <div className="stui-modal-overlay">
      <div className="stui-modal stui-modal-md">
        <div className="stui-modal-header">
          <h3><Zap size={16} className="stui-imgupload-title-icon" />CMD_A Wiring &amp; Generate</h3>
          <button className="stui-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="stui-modal-body">

          {/* PLC Name */}
          <div className="stui-field-group stui-mb-lg">
            <label>PLC / Device Name</label>
            <input
              type="text"
              value={plcName}
              onChange={e => setPlcName(e.target.value)}
              placeholder="e.g. FC1_PLC"
              className="stui-field-input"
              autoFocus
            />
          </div>

          {/* CMD_A wiring */}
          {candidates && (candidates.ioaiTags.length > 0 || candidates.iodiTags.length > 0) && (
            <div style={{ borderTop: 'var(--border-width) solid var(--color-border-subtle)', paddingTop: 'var(--spacing-lg)' }}>
              <p style={{ margin: '0 0 var(--spacing-md)', fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
                Assign CMD_A sources for each SIM tag. Leave at <strong>0</strong> to use the default.
              </p>

              {candidates.ioaiTags.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', textTransform: 'uppercase', marginBottom: 'var(--spacing-sm)' }}>
                    Analog Inputs (REAL)
                  </div>
                  <div className="stui-data-grid-container stui-mb-md">
                    <div className="stui-data-grid-scroll-area">
                      <table className="stui-data-grid stui-data-grid-auto">
                        <thead>
                          <tr>
                            <th>Tag</th>
                            <th>CMD_A Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {candidates.ioaiTags.map(tag => (
                            <tr key={tag.name}>
                              <td title={tag.name}>{tag.name}</td>
                              <td>
                                <StuiSelect
                                  value={cmdAMap[tag.name] || '0'}
                                  onChange={e => setCmdAMap(prev => ({ ...prev, [tag.name]: e.target.value }))}
                                  className="stui-field-input stui-field-input-xs"
                                >
                                  {candidates.ioaiCandidates.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </StuiSelect>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {candidates.iodiTags.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', textTransform: 'uppercase', marginBottom: 'var(--spacing-sm)' }}>
                    Discrete Inputs (BOOL)
                  </div>
                  <div className="stui-data-grid-container">
                    <div className="stui-data-grid-scroll-area">
                      <table className="stui-data-grid stui-data-grid-auto">
                        <thead>
                          <tr>
                            <th>Tag</th>
                            <th>CMD_A Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {candidates.iodiTags.map(tag => (
                            <tr key={tag.name}>
                              <td title={tag.name}>{tag.name}</td>
                              <td>
                                <StuiSelect
                                  value={cmdAMap[tag.name] || '0'}
                                  onChange={e => setCmdAMap(prev => ({ ...prev, [tag.name]: e.target.value }))}
                                  className="stui-field-input stui-field-input-xs"
                                >
                                  {candidates.iodiCandidates.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </StuiSelect>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {error && (
            <div className="import-val-result fail stui-mt-md">
              <div className="import-val-summary">
                <AlertTriangle size={14} />
                <span>{error}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="stui-modal-footer">
          <button
            onClick={onClose}
            disabled={generating}
            className="ct-btn ct-btn-secondary"
          >
            Cancel
          </button>
          <button
            className="ct-btn ct-btn-primary"
            onClick={handleGenerate}
            disabled={generating || !plcName.trim()}
          >
            {generating ? 'Generating...' : 'Generate SIM Tags'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SimulationTagGenerationPage() {
  const { dbSlug: slug } = useDatabase();
  const { trackTransfer } = useDataTransfer();

  const [mode, setMode] = useState('B');
  const [system, setSystem] = useState('IGN');
  const [version, setVersion] = useState('');
  const [versions, setVersions] = useState([]);

  // Mode B state
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const fileInputRef = useRef(null);

  // Mode A state
  const [parent, setParent] = useState('');
  const [parents, setParents] = useState([]);

  // CMD_A wiring (Mode B)
  const [candidates, setCandidates] = useState(null);
  const [cmdAMap, setCmdAMap] = useState({});

  // Popup (Mode B)
  const [showPopup, setShowPopup] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [generateError, setGenerateError] = useState('');

  // Load versions when system changes
  useEffect(() => {
    simulationTagApi.getVersions(system)
      .then(res => {
        const vList = res.data?.versions || [];
        setVersions(vList);
        setVersion(vList[0] || '');
      })
      .catch(() => { setVersions([]); setVersion(''); });
  }, [system]);

  // Load parents for Mode A
  useEffect(() => {
    if (mode !== 'A' || !slug) return;
    simulationTagApi.getParents(slug)
      .then(res => {
        const pList = res.data || [];
        setParents(pList);
        setParent(pList[0] || '');
      })
      .catch(() => { setParents([]); setParent(''); });
  }, [mode, slug]);

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    setFile(f);
    setFileName(f.name);
    setGenerateResult(null);
    setGenerateError('');
    setCandidates(null);
    setCmdAMap({});
    setParsing(true);

    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await simulationTagApi.getCandidates(fd);
      setCandidates(res.data);
      const init = {};
      (res.data.ioaiTags || []).forEach(t => { init[t.name] = '0'; });
      (res.data.iodiTags || []).forEach(t => { init[t.name] = '0'; });
      setCmdAMap(init);
      setShowPopup(true);
    } catch {
      // Non-fatal — user can still open popup manually
    } finally {
      setParsing(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleModeChange = (m) => {
    setMode(m);
    setGenerateResult(null);
    setGenerateError('');
  };

  const handleGenerate = async (plcName, cmdAMapArg) => {
    if (system === 'CIM' && !version) { setGenerateError('Select a version.'); return; }

    // Mode A: use parent as plcName
    const resolvedPlcName = mode === 'A' ? parent : plcName;

    if (mode === 'B' && !file) { setGenerateError('Upload a CSV file first.'); return; }
    if (mode === 'A' && !parent) { setGenerateError('Select a parent.'); return; }

    setGenerating(true);
    setGenerateError('');
    setGenerateResult(null);

    const formData = new FormData();
    formData.append('mode', mode);
    formData.append('system', system);
    formData.append('version', system === 'IGN' ? 'IGN' : version);
    formData.append('plcName', resolvedPlcName.trim());
    if (mode === 'B') {
      formData.append('file', file);
      formData.append('populateIoAddresses', true);
      const hasWiring = Object.values(cmdAMapArg || {}).some(v => v && v !== '0');
      if (hasWiring) formData.append('cmdAMapJson', JSON.stringify(cmdAMapArg));
    } else {
      formData.append('parent', parent);
    }

    try {
      const res = await simulationTagApi.generate(slug, formData);
      setGenerateResult(res.data);
      setShowPopup(false);
      for (const out of describeOutputs(res.data)) {
        await trackTransfer({ name: out.filename, format: out.format, rows: out.rows }, async () => {
          triggerDownload(out.content, out.filename);
        });
      }
    } catch (err) {
      setGenerateError(err.response?.data || err.message || 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const fileLabels = system === 'CIM'
    ? ['PLC Import CSV (SIM_ datatypes)', 'IEC ST Input Buffer', 'CIM Import CSV (simplified)']
    : ['PLC Import CSV (SIM_ datatypes)', 'IEC ST Input Buffer', 'IGN First Pass CSV'];

  return (
    <div className="dev-module import-template-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Cpu size={20} className="stui-imgupload-title-icon" />
            Simulation Tag Generation
          </h1>
          <span className="dev-module-meta">OTS SIM_ tag set — PLC Import, IEC ST, SCADA Import</span>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="import-steps-layout" style={{ maxWidth: '900px', alignItems: 'flex-start' }}>

          {/* ═══════════ TILE 1: CONFIGURE ═══════════ */}
          <div className="stui-step-panel" style={{ maxWidth: '440px' }}>
            <div className="stui-step-header">
              <span className="stui-step-number">1</span>
              <h3>Configuration</h3>
            </div>

            {/* Mode toggle */}
            <div className="stui-field-group stui-mb-md">
              <label>Input Mode</label>
              <div className="stui-segmented">
                {[['B', 'CSV Upload (Tool 2)'], ['A', 'Database']].map(([m, lbl]) => (
                  <button
                    key={m}
                    onClick={() => handleModeChange(m)}
                    className={`stui-segmented-btn${mode === m ? ' selected' : ''}`}
                  >{lbl}</button>
                ))}
              </div>
            </div>

            {/* SCADA System toggle */}
            <div className="stui-field-group stui-mb-md">
              <label>SCADA System</label>
              <div className="stui-segmented">
                {['CIM', 'IGN'].map(s => (
                  <button
                    key={s}
                    onClick={() => { setSystem(s); setGenerateResult(null); setGenerateError(''); }}
                    className={`stui-segmented-btn${system === s ? ' selected' : ''}`}
                  >{s === 'CIM' ? 'Cimplicity' : 'Ignition'}</button>
                ))}
              </div>
            </div>

            {/* Version — CIM only */}
            {system === 'CIM' && (
              <div className="stui-field-group stui-mb-md">
                <label>Version</label>
                <StuiSelect value={version} onChange={e => setVersion(e.target.value)} className="stui-field-input">
                  {versions.map(v => <option key={v} value={v}>{v}</option>)}
                </StuiSelect>
              </div>
            )}

            {/* Mode B: File upload */}
            {mode === 'B' && (
              <div className="stui-field-group stui-mb-md">
                <label>Tool 2 Output CSV</label>
                <div
                  className={`import-dropzone ${dragOver ? 'drag-over' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => !parsing && fileInputRef.current?.click()}
                  style={{ cursor: parsing ? 'wait' : 'pointer' }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="stui-hidden"
                    onChange={e => handleFile(e.target.files[0])}
                  />
                  {parsing
                    ? <><Loader2 size={22} className="stui-spinning" /><span>Parsing CSV...</span></>
                    : <><Upload size={28} /><span>{fileName || 'Drop .csv file here or click to browse'}</span></>
                  }
                </div>
                {file && !parsing && (
                  <button
                    onClick={() => setShowPopup(true)}
                    style={{
                      marginTop: 'var(--spacing-sm)', width: '100%', padding: 'var(--spacing-sm) 0',
                      border: `2px solid var(--color-primary)`,
                      borderRadius: 'var(--radius-sm)', background: 'var(--color-bg-white)',
                      color: 'var(--color-primary-text)', fontWeight: 600,
                      fontSize: 'var(--text-sm)', cursor: 'pointer'
                    }}
                  >
                    <Zap size={14} style={{ marginRight: 'var(--spacing-sm)', verticalAlign: 'middle' }} />
                    Configure Wiring &amp; Generate
                  </button>
                )}
              </div>
            )}

            {/* Mode A: Parent selection + Generate */}
            {mode === 'A' && (
              <>
                <div className="stui-field-group stui-mb-md">
                  <label>Parent</label>
                  <StuiSelect value={parent} onChange={e => setParent(e.target.value)} className="stui-field-input">
                    {parents.map(p => <option key={p} value={p}>{p}</option>)}
                  </StuiSelect>
                </div>
                <button
                  className="stui-btn stui-btn-primary stui-w-full"
                  onClick={() => handleGenerate(parent, {})}
                  disabled={generating || !parent}
                >
                  {generating
                    ? <><Loader2 size={16} className="stui-spinning stui-mr-sm" />Generating...</>
                    : <><Cpu size={16} className="stui-mr-sm" />Generate SIM Tags</>
                  }
                </button>
              </>
            )}
          </div>

          {/* ═══════════ TILE 2: STATUS & OUTPUT ═══════════ */}
          <div className="stui-step-panel" style={{ maxWidth: '360px' }}>
            <div className="stui-step-header">
              <span className="stui-step-number">2</span>
              <h3>Output</h3>
            </div>

            {/* Outputs description */}
            <div className="stui-inline-notice stui-mb-md" style={{ lineHeight: 1.6 }}>
              <strong>Outputs (3 files):</strong>
              <ul style={{ margin: 'var(--spacing-xs) 0 0', paddingLeft: 'var(--spacing-lg)' }}>
                {fileLabels.map(l => <li key={l}>{l}</li>)}
              </ul>
              <div style={{ marginTop: 'var(--spacing-sm)', paddingTop: 'var(--spacing-sm)', borderTop: 'var(--border-width) solid var(--color-border-subtle)', color: 'var(--color-gray-500)' }}>
                Tags are named <code>&#123;original&#125;_SIM_AOD</code> with <code>SIM_</code> prefixed datatypes.
                Delete by filtering on "SIM" after OTS testing.
              </div>
            </div>

            {generateError && (
              <div className="import-val-result fail">
                <div className="import-val-summary">
                  <AlertTriangle size={14} />
                  <span>{generateError}</span>
                </div>
              </div>
            )}

            {generateResult && (
              <div className="import-val-result pass">
                <div className="import-val-summary">
                  <CheckCircle size={18} />
                  <span>Complete &mdash; 3 files downloading</span>
                </div>
                <div className="import-val-section">
                  <div className="stui-text-xs" style={{ lineHeight: 1.8 }}>
                    <div>SIM tags: <strong>{generateResult.simTagCount}</strong></div>
                    <div>ST functions: <strong>{generateResult.stFunctionCount}</strong></div>
                  </div>
                  <div className="stui-text-xs" style={{ marginTop: 'var(--spacing-sm)', borderTop: 'var(--border-width) solid var(--color-border-subtle)', paddingTop: 'var(--spacing-sm)' }}>
                    {[
                      { name: generateResult.fileNameA },
                      { name: generateResult.fileNameB },
                      ...(generateResult.fileC ? [{ name: generateResult.fileNameC }] : [])
                    ].map(({ name }) => (
                      <div key={name} className="stui-break-all">&#8595; {name}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ═══════════ POPUP: CMD_A Wiring & Generate (Mode B) ═══════════ */}
      {showPopup && (
        <WiringPopup
          candidates={candidates}
          cmdAMap={cmdAMap}
          setCmdAMap={setCmdAMap}
          onGenerate={handleGenerate}
          onClose={() => setShowPopup(false)}
          generating={generating}
          error={generateError}
        />
      )}

    </div>
  );
}
